import api from './api';

export interface SearchQuery {
  query_string: string;
  search_fields?: string[];
  date_range_start?: string | null;
  date_range_end?: string | null;
  country_codes?: string[];
  classification_codes?: string[];
  max_results?: number;
  language?: string;
}

export interface SearchDocument {
  id: string;
  document_id: string;
  title: string;
  applicants: string[];
  inventors: string[];
  publication_date?: string;
  country?: string;
  doc_number?: string;
  kind?: string;
  abstract?: string;
  relevance_score?: number;
  classification_codes: string[];
  is_analyzed: boolean;
  analysis_summary?: string;
  tags: string[];
}

export interface SearchSession {
  id: string;
  sub_project_id: string;
  session_name: string;
  description?: string;
  search_service: string;
  status: string;
  search_query: SearchQuery;
  total_results_found: number;
  results_retrieved: number;
  documents: SearchDocument[];
  search_duration_seconds?: number;
  api_calls_made: number;
  error_message?: string;
  created_date: string;
  started_date?: string;
  completed_date?: string;
  last_updated: string;

  // Legacy fields for backward compatibility
  request?: string;
  returned_documents?: string[];
}

export interface CreateSearchSessionRequest {
  sub_project_id: string;
  session_name: string;
  description?: string;
  search_service: string;
  search_query: SearchQuery;
}

// ---------------------------------------------------------------------------
// SearchRecord — lightweight record for RAGS/DPDS/OPS searches
// ---------------------------------------------------------------------------

export type SearchType = 'rags' | 'dpds' | 'ops_agent' | 'ops_direct';

export interface SearchRecord {
  id: string;
  sub_project_id: string;
  project_id?: string | null;
  user_id?: string | null;
  search_type: SearchType;
  source_kb_id?: string | null;
  destination_kb_id?: string | null;
  intent?: string | null;
  query_text?: string | null;
  kw_query?: string | null;
  sm_query?: string | null;
  ops_cql?: string | null;
  result_doc_ids: string[];
  saved_doc_ids: string[];
  params: Record<string, unknown>;
  created_date: string;
}

export interface SearchRecordCreate {
  sub_project_id: string;
  project_id?: string | null;
  search_type: SearchType;
  source_kb_id?: string | null;
  destination_kb_id?: string | null;
  intent?: string | null;
  query_text?: string | null;
  kw_query?: string | null;
  sm_query?: string | null;
  ops_cql?: string | null;
  result_doc_ids?: string[];
  saved_doc_ids?: string[];
  params?: Record<string, unknown>;
}

export const searchService = {
  async getSessionsForSubProject(subProjectId: string): Promise<SearchSession[]> {
    const response = await api.get<SearchSession[]>(`/search-sessions/sub-project/${subProjectId}`);
    return response.data;
  },

  async getSession(sessionId: string): Promise<SearchSession> {
    const response = await api.get<SearchSession>(`/search-sessions/${sessionId}`);
    return response.data;
  },

  async createSession(data: CreateSearchSessionRequest): Promise<SearchSession> {
    const response = await api.post<SearchSession>('/search-sessions/', data);
    return response.data;
  },

  async executeSearch(sessionId: string): Promise<SearchSession> {
    const response = await api.post<SearchSession>(`/search-sessions/${sessionId}/execute`);
    return response.data;
  },

  async deleteSession(sessionId: string): Promise<void> {
    await api.delete(`/search-sessions/${sessionId}`);
  },

  async saveRagSearchSession(subProjectId: string, data: {
    sub_project_id: string;
    kb_id: string;
    session_name?: string;
    description?: string;
    query_text?: string;
    kw_query?: string;
    sm_query?: string;
    search_mode: string;
    top_k?: number;
    top_n?: number;
    enable_reranking?: boolean;
    selected_feature_ids?: string[];
    document_ids: string[];
    search_metadata?: Record<string, unknown>;
  }): Promise<SearchSession> {
    const response = await api.post<SearchSession>(`/sub-projects/${subProjectId}/search/save-rag-session`, data);
    return response.data;
  },

  // SearchRecord API
  async createSearchRecord(subProjectId: string, data: SearchRecordCreate): Promise<SearchRecord> {
    const response = await api.post<SearchRecord>(`/sub-projects/${subProjectId}/search-records`, data);
    return response.data;
  },

  async listSearchRecords(subProjectId: string, skip = 0, limit = 50): Promise<SearchRecord[]> {
    const response = await api.get<SearchRecord[]>(`/sub-projects/${subProjectId}/search-records`, { params: { skip, limit } });
    return response.data;
  },

  async getSearchRecord(subProjectId: string, recordId: string): Promise<SearchRecord> {
    const response = await api.get<SearchRecord>(`/sub-projects/${subProjectId}/search-records/${recordId}`);
    return response.data;
  },

  async updateSearchRecord(subProjectId: string, recordId: string, savedDocIds: string[]): Promise<SearchRecord> {
    const response = await api.patch<SearchRecord>(`/sub-projects/${subProjectId}/search-records/${recordId}`, { saved_doc_ids: savedDocIds });
    return response.data;
  },

  async deleteSearchRecord(subProjectId: string, recordId: string): Promise<void> {
    await api.delete(`/sub-projects/${subProjectId}/search-records/${recordId}`);
  },
};

